import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Card,
  Chip,
  IconButton,
  TextField,
  Avatar,
  CircularProgress,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

/**
 * ConseillerChat
 * --------------
 * Chat sticky avec le "Life Agent" (signature unifiée via t('dashboard.lifeAgentSignature')).
 * IMPORTANT (cf. bug i18n du conseil du jour) :
 * l'appel backend doit IMPÉRATIVEMENT inclure la locale utilisateur
 * pour éviter les réponses dans la mauvaise langue.
 */
function ConseillerChat({ messages: initial, suggestedPrompts, onSend, userLocale, agentSignature }) {
  const { t } = useTranslation('layout')
  const [messages, setMessages] = useState(initial)
  const [input, setInput] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isThinking])

  const signature = agentSignature || t('dashboard.lifeAgentSignature')

  // LAP-CP07 — Fallback démo réutilisable. Utilisé soit quand onSend n'est
  // pas fourni, soit quand l'endpoint backend rejette (404 / 5xx hors 402,
  // qui lui ouvre la modale "Crédits insuffisants" globale via utils/api.js).
  const appendDemoReply = () => {
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        {
          role: 'agent',
          content: t('conseillerPatrimonial.chat.demoReply'),
          source: signature,
          timestamp: new Date().toISOString(),
          locale: userLocale,
        },
      ])
      setIsThinking(false)
    }, 1200)
  }

  const send = (text) => {
    if (!text?.trim()) return
    const userMsg = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }
    setMessages((m) => [...m, userMsg])
    setInput('')
    setIsThinking(true)

    if (onSend) {
      // LAP-CP07 — handleChatSend du parent gère le billing (1 crédit/msg)
      // + le refresh wallet. Si l'endpoint rejette (404 endpoint pas exposé,
      // 5xx, etc.), on retombe sur la réponse démo pour ne pas casser l'UX.
      // Le cas HTTP 402 (crédits insuffisants) est intercepté par utils/api.js
      // qui ouvre directement la modale globale — pas besoin de le gérer ici.
      onSend(text, userLocale)
        .then((reply) => {
          if (reply && reply.content) {
            setMessages((m) => [...m, reply])
            setIsThinking(false)
          } else {
            // Réponse vide → fallback démo
            appendDemoReply()
          }
        })
        .catch(() => {
          appendDemoReply()
        })
    } else {
      appendDemoReply()
    }
  }

  const localeShort = userLocale ? userLocale.split('-')[0] : 'fr'
  const languageLabel = t(`conseillerPatrimonial.chat.lang.${localeShort}`, { defaultValue: localeShort })

  return (
    <Card
      sx={{
        background: 'linear-gradient(180deg, #0e1a36 0%, #0a1429 100%)',
        border: '1px solid rgba(126,156,219,0.25)',
        borderRadius: 3,
        p: 0,
        color: 'white',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 540,
      }}
    >
      <Box
        sx={{
          p: 2.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(126,156,219,0.2)',
            color: '#7e9cdb',
            width: 40,
            height: 40,
          }}
        >
          <AutoAwesomeIcon />
        </Avatar>
        <Box flex={1}>
          <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {signature}
          </WTypography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#4ade80',
                boxShadow: '0 0 8px #4ade80',
              }}
            />
            <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
              {t('conseillerPatrimonial.chat.online', { language: languageLabel })}
            </WTypography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((m, i) => (
          <Message key={i} message={m} />
        ))}
        {isThinking && (
          <Box display="flex" gap={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'rgba(126,156,219,0.2)',
                color: '#7e9cdb',
                width: 32,
                height: 32,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Box
              sx={{
                bgcolor: 'rgba(126,156,219,0.1)',
                px: 2,
                py: 1.2,
                borderRadius: 2,
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <CircularProgress size={14} sx={{ color: '#7e9cdb' }} />
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', ml: 1 }}>
                {t('conseillerPatrimonial.chat.thinking')}
              </WTypography>
            </Box>
          </Box>
        )}
        <div ref={endRef} />
      </Box>

      {suggestedPrompts && suggestedPrompts.length > 0 && (
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}
        >
          {suggestedPrompts.map((p) => (
            <Chip
              key={p}
              label={p}
              onClick={() => send(p)}
              size="small"
              sx={{
                bgcolor: 'rgba(126,156,219,0.12)',
                color: '#a8c1f0',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { bgcolor: 'rgba(126,156,219,0.25)' },
              }}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={t('conseillerPatrimonial.chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(input)
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.04)',
              color: 'white',
              borderRadius: 2,
              fontSize: 14,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
            },
          }}
        />
        <IconButton
          onClick={() => send(input)}
          sx={{
            bgcolor: '#3b82f6',
            color: 'white',
            '&:hover': { bgcolor: '#2563eb' },
            '&:disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' },
          }}
          disabled={!input.trim() || isThinking}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  )
}

function Message({ message }) {
  const isUser = message.role === 'user'
  return (
    <Box display="flex" gap={1.5} flexDirection={isUser ? 'row-reverse' : 'row'}>
      <Avatar
        sx={{
          bgcolor: isUser ? 'rgba(99,123,177,0.3)' : 'rgba(126,156,219,0.2)',
          color: isUser ? '#dbe5f7' : '#7e9cdb',
          width: 32,
          height: 32,
        }}
      >
        {isUser ? <PersonIcon sx={{ fontSize: 16 }} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
      </Avatar>
      <Box
        sx={{
          maxWidth: '85%',
          bgcolor: isUser ? 'rgba(59,130,246,0.18)' : 'rgba(126,156,219,0.1)',
          border: `1px solid ${isUser ? 'rgba(59,130,246,0.3)' : 'rgba(126,156,219,0.2)'}`,
          px: 2,
          py: 1.5,
          borderRadius: 2,
        }}
      >
        <WTypography
          variant="body2"
          sx={{ color: '#dbe5f7 !important', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
        >
          {message.content}
        </WTypography>
        {message.source && (
          <WTypography
            variant="caption"
            sx={{
              color: '#7e9cdb !important',
              display: 'block',
              mt: 0.5,
              fontStyle: 'italic',
            }}
          >
            — {message.source}
          </WTypography>
        )}
      </Box>
    </Box>
  )
}

export default ConseillerChat
